import type { InputHTMLAttributes, ReactNode, Ref, TextareaHTMLAttributes } from 'react'
import { useId, useLayoutEffect, useRef } from 'react'
import { haptic } from '../telegram/haptics.ts'

interface FieldProps {
  label?: ReactNode
  hint?: ReactNode
  error?: ReactNode
  children: (id: string) => ReactNode
}

/** Подпись + поле + подсказка. id прокидывается в поле для связи label↔input. */
export function Field({ label, hint, error, children }: FieldProps) {
  const id = useId()
  return (
    <div className="field">
      {label && (
        <label className="field__label" htmlFor={id}>
          {label}
        </label>
      )}
      {children(id)}
      {error ? (
        <div className="field__error">{error}</div>
      ) : hint ? (
        <div className="field__hint">{hint}</div>
      ) : null}
    </div>
  )
}

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  ref?: Ref<HTMLInputElement>
  variant?: 'default' | 'title' | 'inline' | 'time'
}

export function Input({ variant = 'default', className, ref, ...rest }: InputProps) {
  const cls = ['input', variant !== 'default' && `input--${variant}`, className].filter(Boolean).join(' ')
  return <input ref={ref} className={cls} {...rest} />
}

interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  ref?: Ref<HTMLTextAreaElement>
  /** Растёт по содержимому. */
  autoGrow?: boolean
}

export function Textarea({ autoGrow, className, ref, value, ...rest }: TextareaProps) {
  const inner = useRef<HTMLTextAreaElement | null>(null)
  // подгоняем высоту после каждого рендера (значение могло смениться)
  useLayoutEffect(() => {
    const el = inner.current
    if (!autoGrow || !el) return
    el.style.height = 'auto'
    el.style.height = `${el.scrollHeight + 2}px`
  })
  const setRef = (el: HTMLTextAreaElement | null): void => {
    inner.current = el
    if (typeof ref === 'function') ref(el)
    else if (ref) ref.current = el
  }
  const cls = ['textarea', autoGrow && 'textarea--auto', className].filter(Boolean).join(' ')
  return <textarea ref={setRef} className={cls} value={value} {...rest} />
}

interface SwitchRowProps {
  label: ReactNode
  hint?: ReactNode
  checked: boolean
  onChange: (v: boolean) => void
  disabled?: boolean
}

/** Строка с переключателем — вся строка кликабельна (label). */
export function SwitchRow({ label, hint, checked, onChange, disabled }: SwitchRowProps) {
  const id = useId()
  return (
    <label className="switch-row" htmlFor={id}>
      <span className="cell__main">
        <span className="cell__title">{label}</span>
        {hint && <span className="cell__subtitle">{hint}</span>}
      </span>
      <span className="switch">
        <input
          id={id}
          type="checkbox"
          role="switch"
          aria-checked={checked}
          checked={checked}
          disabled={disabled}
          onChange={(e) => {
            haptic.selection()
            onChange(e.target.checked)
          }}
        />
        <span className="switch__track" />
      </span>
    </label>
  )
}

interface CheckboxProps {
  checked: boolean
  onChange: (v: boolean) => void
  label: string
  disabled?: boolean
}

export function Checkbox({ checked, onChange, label, disabled }: CheckboxProps) {
  return (
    <span className="checkbox">
      <input
        type="checkbox"
        aria-label={label}
        checked={checked}
        disabled={disabled}
        onChange={(e) => {
          haptic.selection()
          onChange(e.target.checked)
        }}
      />
      <span className="checkbox__box">
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <path
            d="M3.5 8.5l3 3 6-6.5"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
    </span>
  )
}

interface StepperProps {
  value: number
  min: number
  max: number
  onChange: (v: number) => void
  label: string
  format?: (v: number) => string
}

export function Stepper({ value, min, max, onChange, label, format }: StepperProps) {
  const set = (v: number): void => {
    const next = Math.max(min, Math.min(max, v))
    if (next !== value) {
      haptic.selection()
      onChange(next)
    }
  }
  return (
    <fieldset className="stepper" aria-label={label}>
      <button
        type="button"
        className="stepper__btn"
        aria-label="Меньше"
        disabled={value <= min}
        onClick={() => set(value - 1)}
      >
        −
      </button>
      <output className="stepper__value" aria-live="polite">
        {format ? format(value) : value}
      </output>
      <button
        type="button"
        className="stepper__btn"
        aria-label="Больше"
        disabled={value >= max}
        onClick={() => set(value + 1)}
      >
        +
      </button>
    </fieldset>
  )
}
